"use client";

import * as React from "react";
import Link from "next/link";
import { motion } from "motion/react";
import { AlertTriangle, TrendingDown, ShieldAlert, XCircle, AlertCircle, Activity } from "lucide-react";
import { Section } from "@/components/layout/section";
import { SectionHeader } from "@/components/ui/section-header";

const PROBLEMS = [
  { icon: AlertTriangle, title: "Excellence is treated as a value, not a system.", body: "Organizations print 'Excellence' on their walls and then achieve it only sometimes. Values are aspirations. Systems are mechanisms. The result is inconsistency dressed up as ambition.", severity: "Critical", accent: "red", from: "from-red/15 to-red/0", iconBg: "bg-red/20 border-red/30", iconColor: "text-red", number: "01" },
  { icon: TrendingDown, title: "Talent is systematically misallocated.", body: "The world selects leaders on the wrong signals — confidence mistaken for competence, volume mistaken for capability, self-promotion mistaken for results. The largest, most reliable pool of overlooked talent is the one half of humanity the world is still not betting on.", severity: "High Risk", accent: "coral", from: "from-coral/15 to-coral/0", iconBg: "bg-coral/20 border-coral/30", iconColor: "text-coral", number: "02" },
  { icon: Activity, title: "Execution gaps at the top.", body: "Vision gets the glory. Strategy gets the credit. Execution — the unglamorous, disciplined, systematized work of actually making things run — is treated as the boring part that follows the exciting part. Organizations fail not because they lack vision but because they lack operators.", severity: "High Risk", accent: "amber-gold", from: "from-amber-gold/15 to-amber-gold/0", iconBg: "bg-amber-gold/20 border-amber-gold/30", iconColor: "text-amber-gold", number: "03" },
  { icon: ShieldAlert, title: "The cost of getting it wrong at sovereign scale.", body: "When the client is a government, a royal household, or a political leader facing an existential contest, an operational failure is not a missed quarterly target. It is a national consequence. The gap between excellence-as-aspiration and excellence-as-system is measured in stability, reputation, and lives affected.", severity: "Critical", accent: "denim", from: "from-denim/15 to-denim/0", iconBg: "bg-denim/20 border-denim/30", iconColor: "text-denim", number: "04" },
  { icon: XCircle, title: "The security vacuum.", body: "In a world where digital infrastructure underpins every critical function — from elections to defense to financial systems — the absence of sovereign-grade security is not a gap. It is a liability. Organizations that rely on third-party tools are building on foundations they do not control.", severity: "Critical", accent: "crimson", from: "from-crimson/15 to-crimson/0", iconBg: "bg-crimson/20 border-crimson/30", iconColor: "text-crimson", number: "05" },
];

export function ProblemSection() {
  return (
    <Section id="problem" ariaLabel="The Problem She Solves" background="default">
      <SectionHeader
        icon={AlertCircle}
        overline="The Problem"
        overlineClassName="text-red"
        pillClassName="bg-red/15 border-red/30 text-red"
        title={
          <>
            The Operational Excellence <span className="text-gradient-energy">Crisis.</span>
          </>
        }
        subtitle="Most organizations do not realize the crisis until the cost of inaction has already been paid."
      />

      <div className="space-y-4">
        {PROBLEMS.map((p, i) => (
          <motion.div
            key={p.title}
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-30px" }}
            transition={{ duration: 0.5, delay: i * 0.1 }}
            className={`group glass-card rounded-xl p-6 bg-gradient-to-r ${p.from} border border-current/10`}
          >
            <div className="grid md:grid-cols-[auto_1fr_auto] gap-5 items-center">
              <div className={`size-14 rounded-xl ${p.iconBg} border flex items-center justify-center group-hover:scale-110 transition-transform`}>
                <p.icon className={`size-7 ${p.iconColor}`} />
              </div>
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <span className={`text-caption font-semibold ${p.iconColor}`}>{p.severity}</span>
                  <span className="text-muted-foreground opacity-30">·</span>
                  <span className="text-caption text-muted-foreground">Problem {p.number}</span>
                </div>
                <h3 className="text-heading-md mb-2">{p.title}</h3>
                <p className="text-body-sm text-muted-foreground leading-relaxed">{p.body}</p>
              </div>
              <div className={`hidden md:flex size-16 items-center justify-center rounded-full border-2 border-dashed border-current/20 text-display-sm opacity-30 font-bold ${p.iconColor}`}>
                {p.number}
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      <div className="mt-10 flex flex-wrap items-center justify-center gap-2">
        {[
          { cls: "text-red", label: "Critical" },
          { cls: "text-coral", label: "High Risk" },
          { cls: "text-amber-gold", label: "Moderate" },
          { cls: "text-denim", label: "Systemic" },
          { cls: "text-crimson", label: "Structural" },
        ].map((s) => (
          <span key={s.label} className="glass rounded-full px-3 py-1.5 text-caption font-medium">
            <span className={s.cls}>·</span> <span className="text-muted-foreground ml-1">{s.label}</span>
          </span>
        ))}
      </div>
    </Section>
  );
}
